import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Wallet, BadgeCheck, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatCard } from "@/components/shared/PagePrimitives";
import { formatCZK, formatCZKCompact, formatHours } from "@/lib/format";

// Veřejná kalkulačka úspory (174) — cíl tlačítka z e-mailu kampaně „den 5".
// Model: firma s 10 lidmi ušetří 24,8 h/měs; 30 % je fixní kancelář, 70 % roste s počtem lidí.
const ROWS = [
  ["Sčítání docházky, podklady pro mzdy", 6, 0.5],
  ["Přepisování faktur a účtenek", 5.5, 1],
  ["Telefonáty „kde jsi, co děláš“", 7, 1],
  ["Stavební deník zpětně", 5, 2],
  ["Čísla o zakázkách v Excelu", 6, 0.2],
];
// Ceny bez DPH; fee = jednorázová implementace (backend config.PLANS.setup_fee).
const PLANS = [
  { max: 3, name: "Starter", price: 990, fee: 10900 },
  { max: 15, name: "Profi", price: 3990, fee: 14900 },
  { max: Infinity, name: "Business", price: 6990, fee: 19900 },
];
const scale = (people) => 0.3 + (0.7 * people) / 10;

export default function Calculator() {
  const { user } = useAuth() || {};
  const [people, setPeople] = useState("10");
  const [rate, setRate] = useState("500");
  const p = Math.min(500, Math.max(1, parseInt(people, 10) || 1));
  const r = Math.max(0, parseInt(rate, 10) || 0);
  const k = scale(p);
  const rows = ROWS.map(([label, before, after]) => ({ label, before: before * k, after: after * k }));
  const saved = rows.reduce((s, x) => s + x.before - x.after, 0);
  const plan = PLANS.find((x) => p <= x.max);
  const net = saved * r - plan.price;
  const roi = plan.price ? (saved * r) / plan.price : 0;
  const hidden = p * 8 * r;
  const payback = net > 0 ? Math.ceil(plan.fee / net) : null;   // měsíců do vrácení implementace

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="calculator-page">
      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12 space-y-8">
        <Link to="/" className="inline-flex items-center gap-2.5 font-bold text-lg">
          <img src="/icon-192.png" alt="" className="w-8 h-8 rounded-[9px]" /> Proyekty
        </Link>

        <header className="space-y-2">
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-balance">
            Kolik vám Proyekty vrátí každý měsíc
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Méně papíru, víc klidu. Zadejte počet lidí a cenu hodiny. Výpočet vychází z typické stavební firmy; je to odhad, ne slib.
          </p>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="glass rounded-[14px] p-4">
            <Label htmlFor="calc-people">Lidí ve firmě</Label>
            <Input id="calc-people" data-testid="calc-people" type="number" inputMode="numeric" min={1} max={500}
              value={people} onChange={(e) => setPeople(e.target.value)} className="h-12 mt-1.5 w-full min-w-0" />
          </div>
          <div className="glass rounded-[14px] p-4">
            <Label htmlFor="calc-rate">Cena hodiny práce (Kč)</Label>
            <Input id="calc-rate" data-testid="calc-rate" type="number" inputMode="numeric" min={0} step={50}
              value={rate} onChange={(e) => setRate(e.target.value)} className="h-12 mt-1.5 w-full min-w-0" />
          </div>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Clock} label="Ušetříte" value={formatHours(saved)} sub="každý měsíc" testid="calc-hours" />
          <StatCard icon={Wallet} label="Hodnota času" value={formatCZKCompact(saved * r)} title={formatCZK(saved * r)} sub="za měsíc" />
          <StatCard icon={BadgeCheck} label="Tarif" value={plan.name} sub={`${formatCZK(plan.price)}/měs. bez DPH`} />
          <StatCard icon={TrendingUp} label="Čistě navíc" value={formatCZKCompact(net)} title={formatCZK(net)}
            sub={`návratnost ${roi.toLocaleString("cs-CZ", { maximumFractionDigits: 1 })}×`}
            valueClass={net >= 0 ? "text-success" : "text-destructive"} testid="calc-net" />
        </section>

        <p className="text-sm text-muted-foreground" data-testid="calc-fee">
          K tarifu {plan.name} patří jednorázová implementace <b className="text-foreground whitespace-nowrap tabular-nums">{formatCZK(plan.fee)}</b> bez DPH.{" "}
          {payback
            ? <>Vrátí se vám za <b className="text-foreground whitespace-nowrap">{payback} {payback === 1 ? "měsíc" : payback <= 4 ? "měsíce" : "měsíců"}</b>.</>
            : "Při těchto číslech se vám nevrátí, zkuste zadat reálnou cenu hodiny."}{" "}
          Obsahuje migraci dat, osobní zaškolení týmu i dělníků a přímou linku na zakladatele na 30 dní
          {plan.name === "Starter" ? "" : ", u Profi a Business i napojení na AI asistenta Claude a 7 AI specialistů"}.
          <b className="text-foreground"> Garance 30 dní: když Proyekty nebudou pro vás, vrátíme celou implementaci.</b>
        </p>

        <section className="glass rounded-[14px] p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                <th className="py-2 pr-3 font-bold">Za měsíc</th>
                <th className="py-2 px-3 font-bold text-right whitespace-nowrap">Dnes</th>
                <th className="py-2 pl-3 font-bold text-right whitespace-nowrap">S Proyekty</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.label} className="border-t border-border">
                  <td className="py-2.5 pr-3">{x.label}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">{formatHours(x.before)}</td>
                  <td className="py-2.5 pl-3 text-right tabular-nums whitespace-nowrap">{formatHours(x.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="rounded-[14px] p-4 md:p-5 bg-brand/10 border border-brand/30 space-y-1.5">
          <div className="font-bold">A skrytá ztráta, kterou nevidíte</div>
          <p className="text-sm">
            Jeden zapomenutý den docházky u {p} {p === 1 ? "člověka" : "lidí"} = {formatHours(p * 8)}, které nikdy nevyfakturujete.
            To je <b className="whitespace-nowrap tabular-nums">{formatCZK(hidden)}</b>.
          </p>
        </section>

        <div className="flex flex-col md:flex-row md:justify-end gap-3">
          {user ? (
            <a href="mailto:petr@proyekty.cz?subject=Spu%C5%A1t%C4%9Bn%C3%AD%20Proyekty" data-testid="calc-cta"
              className="inline-flex items-center justify-center h-12 px-6 rounded-[14px] bg-brand text-white font-bold w-full md:w-auto">
              Domluvit spuštění
            </a>
          ) : (<>
            <Link to="/login" className="inline-flex items-center justify-center h-12 px-6 rounded-[14px] bg-card border border-border font-bold w-full md:w-auto">
              Přihlásit se
            </Link>
            <Link to="/register" data-testid="calc-cta"
              className="inline-flex items-center justify-center h-12 px-6 rounded-[14px] bg-brand text-white font-bold w-full md:w-auto">
              Vyzkoušet 14 dní zdarma
            </Link>
          </>)}
        </div>
      </div>
    </div>
  );
}
